import { useEffect, useState } from "react";
import { formatUnits } from "../lib/forecastGrid";

interface Props {
  value: number | undefined;
  editable: boolean;
  muted: boolean;
  onSave: (units: string) => void;
}

// One unit cell: either a plain right-aligned figure (locked months, other
// people's rows, or view-only users) or an always-visible spreadsheet input
// that saves on blur — same look as the Bottom Up monthly grid cells. Local
// text state mirrors the incoming value so a server round trip after save
// can correct it without fighting the user's typing.
export default function ForecastCell({ value, editable, muted, onSave }: Props) {
  const initial = value != null ? value.toLocaleString("en-US") : "";
  const [text, setText] = useState(initial);

  useEffect(() => {
    setText(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial]);

  if (!editable) {
    return (
      <span
        className={`block w-full px-1.5 py-1 text-right text-xs tabular-nums ${muted ? "text-muted-foreground" : "text-foreground"}`}
      >
        {formatUnits(value ?? null)}
      </span>
    );
  }

  return (
    <input
      type="text"
      inputMode="numeric"
      value={text}
      placeholder="—"
      onChange={(e) => {
        const digits = e.target.value.replace(/[^\d]/g, "");
        setText(digits ? parseInt(digits, 10).toLocaleString("en-US") : "");
      }}
      onBlur={() => {
        if (text === initial) return;
        onSave(text.replace(/[^\d]/g, ""));
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
      }}
      style={{ background: "var(--background)", borderColor: "var(--border)", color: "var(--foreground)" }}
      className="w-full rounded-md border px-1.5 py-1 text-right text-xs tabular-nums outline-none transition-colors focus:border-secondary placeholder:text-muted-foreground/50"
    />
  );
}
